import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { LessonModule } from '../../content'

// =============================================================================
// LESSON MODULE METRICS MONTHLY - Lookup table (`cron / metrics-monthly`)
// Grain: one row per (lessonModuleId, month=start-of-month UTC).
// Recomputed each run from LovedLessonModule / LessonModuleView event rows
// (createdAt >= month), UPSERT target [lessonModuleId, month]. Idempotent.
// =============================================================================

export const LessonModuleMetricsMonthly = pgTable(
  'lesson_module_metrics_monthly',
  {
    id: uuid().defaultRandom().primaryKey(),
    lessonModuleId: uuid()
      .references(() => LessonModule.id, { onDelete: 'cascade' })
      .notNull(),
    month: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    likesCount: integer().notNull().default(0),
    viewsCount: integer().notNull().default(0),
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),
  },
  (t) => [
    index('idx_lesson_module_metrics_monthly_module').on(t.lessonModuleId),
    index('idx_lesson_module_metrics_monthly_month').on(t.month),
    index('idx_lesson_module_metrics_monthly_month_likes').on(t.month, t.likesCount),
    index('idx_lesson_module_metrics_monthly_month_views').on(t.month, t.viewsCount),
    unique('uq_lesson_module_metrics_monthly_module_month').on(t.lessonModuleId, t.month),
  ],
)

export const lessonModuleMetricsMonthlyRelations = relations(LessonModuleMetricsMonthly, ({ one }) => ({
  lessonModule: one(LessonModule, {
    fields: [LessonModuleMetricsMonthly.lessonModuleId],
    references: [LessonModule.id],
  }),
}))

export const InsertLessonModuleMetricsMonthlySchema = createInsertSchema(LessonModuleMetricsMonthly).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})
export const UpdateLessonModuleMetricsMonthlySchema = createUpdateSchema(LessonModuleMetricsMonthly).partial().omit({
  id: true,
  lessonModuleId: true,
  month: true,
  createdAt: true,
  updatedAt: true,
})
export const SelectLessonModuleMetricsMonthlySchema = createSelectSchema(LessonModuleMetricsMonthly)

export type InsertLessonModuleMetricsMonthly = typeof LessonModuleMetricsMonthly.$inferInsert
export type UpdateLessonModuleMetricsMonthly = z.infer<typeof UpdateLessonModuleMetricsMonthlySchema>
export type SelectLessonModuleMetricsMonthly = typeof LessonModuleMetricsMonthly.$inferSelect
